<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAudioPlayerStore } from '@/stores'

const audioStore = useAudioPlayerStore()

const elRef = ref<HTMLElement>()
const posX = ref(0)
const posY = ref(0)
let startX = 0
let startY = 0
let originX = 0
let originY = 0
let moved = false

const progressPercent = computed(() => {
  if (!audioStore.duration)
    return 0
  return (audioStore.currentTime / audioStore.duration) * 100
})

function onTouchStart(e: TouchEvent) {
  startX = e.touches[0].clientX
  startY = e.touches[0].clientY
  originX = posX.value
  originY = posY.value
  moved = false
}

function onTouchMove(e: TouchEvent) {
  const dx = e.touches[0].clientX - startX
  const dy = e.touches[0].clientY - startY
  if (Math.abs(dx) > 5 || Math.abs(dy) > 5)
    moved = true

  posX.value = clamp(originX + dx, 0, window.innerWidth - 140)
  posY.value = clamp(originY + dy, 0, window.innerHeight - 48)
}

function onTouchEnd() {
  if (!moved)
    onOpenPopup()
  moved = false
}

function onOpenPopup() {
  audioStore.visible = true
  audioStore.miniMode = false
}

function togglePlay(e?: Event) {
  if (moved) {
    e?.preventDefault()
    return
  }
  audioStore.togglePlay()
}

function onClose(e?: Event) {
  if (moved) {
    e?.preventDefault()
    return
  }
  audioStore.closePlayer()
}

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val))
}

function centerPosition() {
  posX.value = 12
  posY.value = window.innerHeight - 200
}

onMounted(() => {
  centerPosition()
})
</script>

<template>
  <Transition name="audio-mini">
    <div
      v-if="audioStore.miniMode"
      ref="elRef"
      class="fixed z-[101] flex items-center gap-10px pl-6px pr-10px py-4px rounded-full shadow-lg select-none touch-none"
      :style="{
        left: `${posX}px`,
        top: `${posY}px`,
        background: 'rgba(22, 33, 62, 0.95)',
        backdropFilter: 'blur(8px)',
      }"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    >
      <!-- 圆形封面 + 进度环（拖动手柄） -->
      <div class="relative w-36px h-36px cursor-pointer flex-shrink-0">
        <svg class="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="17" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="2.5" />
          <circle
            cx="20" cy="20" r="17" fill="none"
            stroke="var(--primary-color, #9875FB)" stroke-width="2.5"
            stroke-linecap="round"
            :stroke-dasharray="106.8"
            :stroke-dashoffset="106.8 - (106.8 * progressPercent) / 100"
          />
        </svg>
        <img
          :src="audioStore.contentCoverUrl"
          class="audio-mini-cover w-28px h-28px rounded-full object-cover absolute inset-1/2 -translate-x-1/2 -translate-y-1/2"
          :class="{ 'audio-mini-cover--paused': !audioStore.isPlaying || audioStore.audioLoading }"
          alt="cover"
        >
      </div>

      <!-- 播放/暂停 -->
      <button
        class="text-white flex items-center justify-center w-24px h-24px p-0 flex-shrink-0"
        @click.stop="togglePlay"
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <img
          :src="audioStore.audioLoading ? '/images/audio/loading-mini.png' : audioStore.isPlaying ? '/images/audio/pause-small.png' : '/images/audio/play-small.png'"
          class="w-18px h-18px"
          :class="{ 'loading-spin': audioStore.audioLoading }"
          alt=""
        >
      </button>

      <!-- 关闭 -->
      <button
        class="text-white/50 flex items-center justify-center w-16px h-16px p-0 flex-shrink-0"
        @click.stop="onClose"
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <i-icon-park-outline-close class="text-14px" />
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.audio-mini-enter-active,
.audio-mini-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.audio-mini-enter {
  transform: translateY(20px);
  opacity: 0;
}
.audio-mini-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.audio-mini-cover {
  animation: audio-mini-cover-rotate 8s linear infinite;
  animation-play-state: running;
}

.audio-mini-cover--paused {
  animation-play-state: paused;
}

@keyframes audio-mini-cover-rotate {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.loading-spin {
  animation: loading-spin-rotate 2s linear infinite;
}

@keyframes loading-spin-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
